import { randomUUID } from "#/shared/utils/string";

// Used to keep analytics batches in `sessionStorage` from the moment their events are recorded until they are sent, so
// a page that is left or dies before sending them leaves them for the visitor's next page in the same tab and on the
// same origin. The browser discards them when the tab closes. A page on another origin, such as another subdomain, does
// not see them. Other tabs do not share them, except a tab the browser duplicates or a window this page opens with an
// opener, which starts with a copy. `AnalyticsEventClaims` keeps the two from sending the same event.
//
// A batch is held while this page still has it in memory and will send it itself. A batch nothing on this page holds,
// including every batch an earlier page left, is unheld, and `drain` claims only unheld batches. A page that dies
// without a leave event holds nothing afterwards, so the next page claims what it left. Each batch is stored under its
// own key, and removed by `remove` once it is sent, or by `purge` or `drain` once it is unreadable or past `maxAge`.
// Claims are made under a Web Lock when the browser offers one, so a claim runs once the lock is granted (see `drain`).
//
// A batch is `{events, tokens}`, where `tokens` is the `{visit_token, visitor_token}` the events belong to.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventOutbox {
  // @param options {Object}
  //   - `maxBatches`: the most batches held at once
  //   - `maxAge`: the milliseconds after its oldest event's time that a batch stays sendable, which matches
  //     `Ahoy.visit_duration` by default
  //   - `prefix`: the start of every `sessionStorage` key the outbox stores under, and the name of its Web Lock.
  //     Defaults to `sparkle_analytics_outbox:`.
  //   - `onRefused`: called with the batch and the reason, `"full"` or `"denied"`, whenever the outbox cannot store a
  //     batch or rewrite one it holds
  constructor(options) {
    options = options || {};

    this.prefix = options.prefix || "sparkle_analytics_outbox:";
    this.maxBatches = options.maxBatches || 20;
    this.maxAge = options.maxAge || 4 * 60 * 60 * 1000;
    this.onRefused = options.onRefused || function () {};
    this.held = new Set();
  }

  // Stores the batch unheld, and returns whether it was stored. A full outbox, or storage the browser denies, stores nothing.
  store(batch) {
    return Boolean(this.write(batch));
  }

  // Stores the batch held by this page, and returns the key it is stored under. Returns null when the outbox is full or
  // the browser denies storage.
  hold(batch) {
    const key = this.write(batch);

    if (key) {
      this.held.add(key);
    }

    return key;
  }

  // Replaces the batch stored under `key`, keeping whether it is held. Returns whether the browser stored it.
  rewrite(key, batch) {
    try {
      sessionStorage.setItem(key, this.serialize(batch));

      return true;
    } catch {
      this.onRefused(batch, "denied");

      return false;
    }
  }

  // Removes the batch stored under `key`, and stops holding it.
  remove(key) {
    this.held.delete(key);

    try {
      sessionStorage.removeItem(key);
    } catch {
      // A batch the browser will not remove expires with `maxAge`.
    }
  }

  // Stops holding every batch this page holds.
  unholdAll() {
    this.held.clear();
  }

  write(batch) {
    try {
      // An outbox that looks full may hold batches that have expired since a page last purged it.
      if (this.full()) {
        this.purge();
      }

      if (this.full()) {
        this.onRefused(batch, "full");

        return null;
      }

      const key = this.prefix + randomUUID();

      sessionStorage.setItem(key, this.serialize(batch));

      return key;
    } catch {
      this.onRefused(batch, "denied");

      return null;
    }
  }

  serialize(batch) {
    return JSON.stringify({ events: batch.events, tokens: batch.tokens });
  }

  // Claims up to `limit` unheld batches, every one when `limit` is omitted, and passes each one still readable and
  // within `maxAge` to `callback` as `{events, tokens, key}`. A claimed batch is held until the caller removes it once
  // it is sent. A batch that is unreadable or past `maxAge` is removed. Returns a promise of whether any unheld batch
  // is still stored.
  //
  // Under a Web Lock, `callback` runs once the lock is granted, after `drain` has returned. A throw from `callback`, or
  // a failed lock request, resolves the promise to false, and a claimed batch not yet passed to `callback` stays held
  // until `unholdAll`.
  // Without Web Locks, `callback` runs before `drain` returns, and a throw from it propagates to the caller.
  // @param cancelled {Function} optional, checked just before claiming, which under a Web Lock is once the lock is
  //   granted. Returning true claims nothing.
  drain(callback, limit, cancelled) {
    const claim = () => {
      if (!cancelled || !cancelled()) {
        this.claimAll(limit).forEach(callback);
      }

      return this.unheldKeys().length > 0;
    };

    if (navigator.locks) {
      return navigator.locks.request(this.prefix, claim).catch(() => false);
    }

    return Promise.resolve(claim());
  }

  claimAll(limit) {
    const batches = [];

    try {
      this.unheldKeys()
        .slice(0, limit)
        .forEach((key) => {
          const batch = this.parse(sessionStorage.getItem(key));

          if (this.sendable(batch)) {
            this.held.add(key);
            batches.push({ events: batch.events, tokens: batch.tokens, key: key });
          } else {
            sessionStorage.removeItem(key);
          }
        });
    } catch {
      // Returns what was claimed before the browser denied storage.
    }

    return batches;
  }

  // Removes every unheld batch that is past `maxAge` or cannot be read.
  purge() {
    try {
      this.unheldKeys().forEach((key) => {
        if (!this.sendable(this.parse(sessionStorage.getItem(key)))) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // Keeps what is left when the browser denies storage.
    }
  }

  // Returns whether the batch can still be sent: it holds events, each carries a time, and the oldest of them is within
  // `maxAge`. Age counts from the oldest event's time, because an event can wait in the queue and through retries
  // before its batch is stored.
  sendable(batch) {
    if (!batch || !Array.isArray(batch.events) || batch.events.length === 0) {
      return false;
    }

    const times = batch.events.map((event) => event && event.time);

    if (!times.every((time) => Number.isFinite(time))) {
      return false;
    }

    return Date.now() - Math.min(...times) * 1000 < this.maxAge;
  }

  full() {
    return this.keys().length >= this.maxBatches;
  }

  // Returns the keys of the stored batches this page does not hold.
  unheldKeys() {
    return this.keys().filter((key) => !this.held.has(key));
  }

  // Returns the keys of the stored batches, held or not, or none when the browser denies storage.
  keys() {
    const keys = [];

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);

        if (key && key.indexOf(this.prefix) === 0) {
          keys.push(key);
        }
      }
    } catch {
      return [];
    }

    return keys;
  }

  parse(json) {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
}
